/**
 * Product Customization Types
 * These types match the backend ProductDTO customization structure
 */

export enum OptionGroupType {
  SINGLE_CHOICE = 'SINGLE_CHOICE',    // Radio buttons
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE', // Checkboxes
  QUANTITY = 'QUANTITY'                // Number input
}

/**
 * Translation for option group name
 */
export interface OptionGroupTranslation {
  name: string;
}

/**
 * Translation for option name
 */
export interface OptionTranslation {
  name: string;
}

/**
 * Individual option within an option group
 */
export interface ProductOption {
  id: string;
  name: string;
  priceModifier: number;
  ingredientId?: string | null;
  defaultOption: boolean;
  available: boolean;
  allergens?: string[];
  translations?: Record<string, OptionTranslation>;
}

/**
 * Option group - represents a category of choices
 * Example: "Size" with options [Small, Medium, Large]
 */
export interface ProductOptionGroup {
  id: string;
  name: string;
  type: OptionGroupType;
  required: boolean;
  minSelections?: number | null;
  maxSelections?: number | null;
  displayOrder: number;
  options: ProductOption[];
  translations?: Record<string, OptionGroupTranslation>;
}

/**
 * Root container for product customization options
 */
export interface ProductCustomizationOptions {
  optionGroups: ProductOptionGroup[];
}

/**
 * Selected option in an order item
 */
export interface SelectedOption {
  optionGroupId: string;
  optionGroupName: string;
  optionId: string;
  optionName: string;
  quantity: number;
  priceModifier: number;
  ingredientId?: string | null;
}

/**
 * Order item customizations
 */
export interface OrderItemCustomizations {
  selectedOptions: SelectedOption[];
  freeTextNotes?: string | null;
  totalPriceModifier: number;
}

/**
 * Helper type for validation errors
 */
export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Validation result
 */
export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

/**
 * Helper function to get translated option group name
 */
export function getOptionGroupName(
  optionGroup: ProductOptionGroup,
  language: string = 'it'
): string {
  if (optionGroup.translations && optionGroup.translations[language]) {
    return optionGroup.translations[language].name;
  }
  return optionGroup.name;
}

/**
 * Helper function to get translated option name
 */
export function getOptionName(
  option: ProductOption,
  language: string = 'it'
): string {
  if (option.translations && option.translations[language]) {
    return option.translations[language].name;
  }
  return option.name;
}

/**
 * Calculate total price including customization modifiers
 */
export function calculateCustomizedPrice(
  basePrice: number,
  customizations: OrderItemCustomizations | null
): number {
  if (!customizations) {
    return basePrice;
  }
  return basePrice + (customizations.totalPriceModifier || 0);
}

/**
 * Validate selections against option group rules
 */
export function validateSelections(
  optionGroup: ProductOptionGroup,
  selectedOptions: SelectedOption[]
): ValidationResult {
  const errors: ValidationError[] = [];

  // Filter selections for this group
  const groupSelections = selectedOptions.filter(
    s => s.optionGroupId === optionGroup.id
  );

  // Check required
  if (optionGroup.required && groupSelections.length === 0) {
    errors.push({
      field: optionGroup.id,
      message: `${optionGroup.name} è obbligatorio`
    });
  }

  // Check based on type
  switch (optionGroup.type) {
    case OptionGroupType.SINGLE_CHOICE:
      if (groupSelections.length > 1) {
        errors.push({
          field: optionGroup.id,
          message: `${optionGroup.name} permette solo una selezione`
        });
      }
      break;

    case OptionGroupType.MULTIPLE_CHOICE:
      if (optionGroup.minSelections && groupSelections.length < optionGroup.minSelections) {
        errors.push({
          field: optionGroup.id,
          message: `${optionGroup.name} richiede almeno ${optionGroup.minSelections} selezioni`
        });
      }
      if (optionGroup.maxSelections && groupSelections.length > optionGroup.maxSelections) {
        errors.push({
          field: optionGroup.id,
          message: `${optionGroup.name} permette massimo ${optionGroup.maxSelections} selezioni`
        });
      }
      break;

    case OptionGroupType.QUANTITY:
      // For quantity type, validate that quantities are >= 0
      for (const selection of groupSelections) {
        if (selection.quantity < 0) {
          errors.push({
            field: optionGroup.id,
            message: `Quantità non valida per ${selection.optionName}`
          });
        }
      }
      break;
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validate all selections for a product
 */
export function validateAllSelections(
  customizationOptions: ProductCustomizationOptions,
  selectedOptions: SelectedOption[]
): ValidationResult {
  const allErrors: ValidationError[] = [];

  for (const optionGroup of customizationOptions.optionGroups) {
    const result = validateSelections(optionGroup, selectedOptions);
    allErrors.push(...result.errors);
  }

  return {
    valid: allErrors.length === 0,
    errors: allErrors
  };
}

/**
 * Calculate total price modifier from selections
 */
export function calculateTotalPriceModifier(
  selectedOptions: SelectedOption[]
): number {
  return selectedOptions.reduce((total, option) => {
    const quantity = option.quantity || 1;
    return total + (option.priceModifier * quantity);
  }, 0);
}

/**
 * Apply default options to create initial selections
 */
export function applyDefaultOptions(
  customizationOptions: ProductCustomizationOptions
): SelectedOption[] {
  const defaultSelections: SelectedOption[] = [];

  for (const optionGroup of customizationOptions.optionGroups) {
    // For SINGLE_CHOICE groups, select the default option if available
    if (optionGroup.type === OptionGroupType.SINGLE_CHOICE) {
      const defaultOption = optionGroup.options.find(
        opt => opt.defaultOption && opt.available
      );

      if (defaultOption) {
        defaultSelections.push({
          optionGroupId: optionGroup.id,
          optionGroupName: optionGroup.name,
          optionId: defaultOption.id,
          optionName: defaultOption.name,
          quantity: 1,
          priceModifier: defaultOption.priceModifier,
          ingredientId: defaultOption.ingredientId
        });
      }
    }
  }

  return defaultSelections;
}
